/**
 * The `hiring` section — RO-3-07 (task #44300).
 *
 * RO-3-01's section map scopes this item to TWO pages
 * (`docs/rollover/inventory/pages.md` §4 "Hiring — 2 pages, owned by RO-3-07"),
 * and unlike most of L3 the wrapper LOC was honest: re-measuring both with
 * `scripts/rollover-delegate-measure.mjs` on this tree gives `privateDelegateLoc:
 * 0` and `correctedLoc == wrapperLoc` for each (30 and 148), i.e. ×1.0 against a
 * lane that ran as high as ×5.4. Neither page has a single relative or `@/`
 * delegate; both render straight out of `@excellent/ui`.
 *
 * Only ONE of the two becomes a route here, and the other one's absence is the
 * finding, not an omission — see below.
 *
 * ══ 1. `hiring/page.tsx` (30 LOC) — NO ROUTE, deliberately ═══════════════════
 *
 * It is a `redirect()` to `/operations?area=hiring`, and its own header calls
 * itself "the defensive backstop" behind the root layout's modes redirect. That
 * cascade is ALREADY PORTED: `shell/redirects.ts` gate 8 (RO-0-05) runs
 * `resolveModeRedirect` + `modeRedirectUrl` from `__root`'s `beforeLoad`, which
 * fires on every navigation BEFORE any child route resolves. Measured on this
 * tree by calling the real resolver:
 *
 *     /hiring                   ->  /operations?area=hiring
 *     /hiring?sub=jobs          ->  /operations?area=hiring&sub=jobs
 *     /hiring?sub=jobs&view=board -> /operations?area=hiring&sub=jobs
 *     /hiring/jobs/senior-eng   ->  (no redirect: renders in place)
 *
 * So a `/hiring` route would be unreachable code: the root redirects first, in
 * this stack exactly as in Next. Registering one would also make it a LIAR about
 * `?view=` — `modeRedirectUrl` strips `view` (`k !== "view"`), while the Next
 * backstop page forwarded it. Since the cascade wins in BOTH stacks, `view` is
 * dropped in both today, and a route that preserved it would be a behaviour
 * change dressed as a port. `test-hiring-section` pins all four rows above so the
 * "no route needed" claim keeps being true rather than merely having been true.
 *
 * ══ 2. `hiring/jobs/[slug]/page.tsx` (148 LOC) — the REWRITE ════════════════
 *
 * RO-3-01 flagged this one specifically: it "branches its entire render on
 * `headers()` ... the port needs a real design decision, not just 'become an
 * endpoint.'" Three decisions, each recorded where it was made:
 *
 * ── 2a. The operator/public branch keeps its EXACT semantics ──
 *
 * The original chose a view from four signals: `?public=1` forces public,
 * `?internal=1` forces operator, a non-public job is always operator, and
 * otherwise a same-origin referer containing `/hiring` means operator. The first
 * three are query/row state and port unchanged. The fourth is ported to its exact
 * browser twin, `document.referrer` — the same string the `referer` header
 * carried, read where the render now happens.
 *
 * The semantics are preserved rather than improved ON PURPOSE, because they are
 * load-bearing for the one behaviour that must not regress: a candidate arriving
 * cold on a published posting has no same-origin referer, so `isOperator` is
 * false and they get `<PublicJobPage>`. That is the careers page, and it must keep
 * working unauthenticated.
 *
 * WORTH KNOWING, and NOT fixed here: the referer leg is already effectively dead
 * in the current IA. It requires a referring URL containing the substring
 * `/hiring`, and no operator surface has one any more — hiring is section-hosted,
 * so the dashboard is `/operations?area=hiring` (which contains `=hiring`, not
 * `/hiring`), and `/hiring` itself now redirects there. The live operator links
 * are plain `/hiring/jobs/<slug>` (`src/capabilities/hiring/index.tsx:53,88`,
 * `blocks/hiring/job-card.tsx:14`), so an operator clicking a PUBLIC job already
 * lands on the public view in Next today. Retiring that leg in favour of the
 * explicit `?internal=1` convention is a product decision about who the default
 * viewer is; it is filed, not smuggled in under a port.
 *
 * ── 2b. Two reads had to become endpoints first (the #44306 pattern) ──
 *
 * The page called `getJobBySlug()` and `countApplicationsByStage()` in-process
 * while server-rendering. A client route cannot, so both are now mounted:
 * `jobsGetBySlug` and `applicationsStageCounts`. Why they sit on DIFFERENT
 * prefixes is a perimeter decision with real consequences and is documented on
 * the operations themselves (`contracts/hiring-jobs.ts`,
 * `contracts/hiring-applications.ts`) — short version: everything under
 * `/hiring/api/jobs` is anonymously readable, so the operator's pipeline counts
 * deliberately are not there. `listPipelineStages` needed nothing new: the mounted
 * `pipelinesList` already returns `stages` per pipeline from that same helper.
 *
 * ── 2c. The page gains the title the design system requires ──
 *
 * The original operator view rendered NO heading — a status badge, a muted
 * department/location/type line, and two buttons in a hand-rolled `flex
 * justify-between`. There is no `app/hiring/layout.tsx` to have supplied one
 * (the de-product teardown removed it), so the page genuinely had no title. That
 * is CLAUDE.md rule 7, and a NEW file earns no grandfathering — the #44455
 * lesson is that a ported file inherits its source's violations but not its
 * allowance. So the same elements are hosted in `<PageHeader>`: the job title
 * becomes the H1, the meta line becomes `description` with its composition
 * unchanged, and the badges join the buttons in `toolbar`. Nothing is dropped;
 * one missing thing is added.
 */

import * as React from "react";
import { z } from "zod";
import { createRoute } from "@tanstack/react-router";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  HIRING_STAGE_INTENT,
  PageHeader,
  StatStrip,
  StatusBadge,
  type StatStripItem,
} from "@excellent/ui";
import { useApi } from "@excellent/ui/api";
import { RouterLink as Link } from "@excellent/ui/router";
import {
  hiringJobs,
  type JobShape,
} from "@excellent/ui/api/contracts/hiring-jobs";
import { hiringApplications } from "@excellent/ui/api/contracts/hiring-applications";
import { pipelinesList } from "@excellent/ui/api/contracts/hiring-pipelines";
import type { StageKind } from "@excellent/ui/api/contracts/hiring-pipelines";
import { JobActionsBar } from "@excellent/ui/blocks/hiring/job-actions-bar";
import { PublicJobPage } from "@excellent/ui/blocks/hiring/public-job-page";
import { rootRoute } from "./__root";
import { routeSearchSchema } from "../shell/search";

/**
 * `?public=1` / `?internal=1` — the original's two explicit overrides, declared
 * route-local. Both are safe to own: a census of every `search.get(...)` reader in
 * `excellent/` + `packages/` finds these two names read by exactly ONE file, the
 * page being ported. They are also NOT shell-wide, so `routeSearchSchema` accepts
 * them — whereas `area`/`sub`/`view` (which the sibling `/hiring` redirect would
 * have needed) are already claimed at the root and would not compile here.
 *
 * The root search parser safely coerces primitive query values before this
 * route validates them, so flags accept the resulting string/number/boolean
 * union. Only `1`, `"1"`, and `true` enable an override; `?public=0` remains off.
 */
const queryFlag = z.union([z.string(), z.number(), z.boolean()]).optional();
const jobSearchSchema = routeSearchSchema({
  public: queryFlag,
  internal: queryFlag,
});

function queryFlagEnabled(value: string | number | boolean | undefined): boolean {
  return value === "1" || value === 1 || value === true;
}

export const hiringJobRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/hiring/jobs/$slug",
  validateSearch: jobSearchSchema,
  component: JobDetailPage,
});

/**
 * The original's same-origin referer sniff, on the client. `document.referrer` is
 * the browser-side twin of `headers().get("referer")`, and `location.host` of the
 * `host` header, so the expression is the source's character for character.
 *
 * Read through a layout effect rather than at render time because `document` does
 * not exist during SSR/prerender of this bundle, and because the value must not
 * change identity between renders and re-run the branch.
 */
function useSameOriginHiringReferrer(): boolean {
  const [sameOrigin, setSameOrigin] = React.useState(false);
  React.useEffect(() => {
    const referer = document.referrer ?? "";
    const host = window.location.host ?? "";
    setSameOrigin(
      !!host && referer.includes(`://${host}`) && referer.includes("/hiring"),
    );
  }, []);
  return sameOrigin;
}

function JobDetailPage() {
  const { slug } = hiringJobRoute.useParams();
  // #44545: single registration of /hiring/jobs/$slug (stub removed from
  // dynamic-segments) so useSearch() is the route-local jobSearchSchema shape.
  const search = hiringJobRoute.useSearch();
  const sameOrigin = useSameOriginHiringReferrer();

  const { data: job, error, isLoading } = useApi(hiringJobs.getBySlug, {
    params: { slug },
  });

  if (error) {
    // A 404 from `jobsGetBySlug` is the original's `notFound()`. Rendered rather
    // than thrown so the shell's chrome survives — a bad slug is a wrong turn,
    // not a crash (same disposition as `agent-workflow-detail.tsx`).
    return <JobNotFound slug={slug} />;
  }
  if (isLoading || !job) {
    return (
      <div className="px-6 py-8 type-body text-muted-foreground">Loading…</div>
    );
  }

  const forceInternal = queryFlagEnabled(search.internal);
  const forcePublic = queryFlagEnabled(search.public);
  const isOperator =
    !forcePublic && (sameOrigin || forceInternal || !job.is_public);

  if (!isOperator) {
    // Verbatim: the public view is only served for an OPEN, public posting.
    if (!job.is_public || job.status !== "open") return <JobNotFound slug={slug} />;
    return <PublicJobPage job={job} />;
  }

  return <JobOperatorView job={job} />;
}

function JobNotFound({ slug }: { slug: string }) {
  return (
    <div className="flex flex-col gap-3 px-6 py-8">
      <h1 className="type-heading font-medium text-foreground">No such job</h1>
      <p className="type-body text-muted-foreground">
        Nothing is published under <span className="font-mono">{slug}</span>.
      </p>
      <Button asChild variant="outline" className="self-start">
        <Link href="/operations?area=hiring">← Hiring</Link>
      </Button>
    </div>
  );
}

/**
 * The operator view. A separate component so its two reads are unconditional
 * hooks — the public branch above returns before this renders and must not have
 * requested the operator-only data (`applicationsStageCounts` is session-gated,
 * and a public visitor has no business issuing the request at all).
 */
function JobOperatorView({ job }: { job: JobShape }) {
  // `pipelinesList` already returns each pipeline's `stages` from
  // `listPipelineStages()` — the same helper, same ORDER BY position, same
  // archived filter — so the original's direct call needed no new endpoint.
  const { data: pipelines } = useApi(pipelinesList, {});
  const { data: stageCounts } = useApi(hiringApplications.stageCounts, {
    query: { job_id: job.id },
  });

  const stages = React.useMemo(
    () => pipelines?.find((p) => p.id === job.pipeline_id)?.stages ?? [],
    [pipelines, job.pipeline_id],
  );
  const counts = React.useMemo(() => {
    const out: Record<number, number> = {};
    for (const row of stageCounts ?? []) out[row.stage_id] = row.count;
    return out;
  }, [stageCounts]);
  const total = Object.values(counts).reduce((a, b) => a + b, 0);

  // Pipeline summary as a framed board strip — one cell per stage, leading
  // status-intent dot driven by the canonical HIRING_STAGE_INTENT map (the
  // stage `kind`), so the row reads as one anchored component above the
  // pipeline board rather than floating numbers.
  const pipelineStats: StatStripItem[] = stages.map((s) => ({
    title: s.name,
    value: String(counts[s.id] ?? 0),
    description: s.kind.charAt(0).toUpperCase() + s.kind.slice(1),
    intent: HIRING_STAGE_INTENT[s.kind as StageKind],
  }));

  return (
    <div className="flex flex-col gap-4 p-6">
      <PageHeader
        ownTitle
        back={{ label: "Hiring", href: "/operations?area=hiring" }}
        title={job.title}
        description={`${job.department ? job.department : "No department"} · ${
          job.location ?? "No location"
        } · ${job.employment_type ?? "Unspecified"}`}
        toolbar={
          <>
            <StatusBadge domain="job" status={job.status} />
            {job.is_public ? <Badge variant="outline">Public</Badge> : null}
            <Button asChild variant="outline">
              <Link href={`/hiring/jobs/${job.slug}/edit`}>Edit</Link>
            </Button>
            <Button asChild>
              <Link href={`/hiring/jobs/${job.slug}/pipeline`}>
                Open pipeline ({total})
              </Link>
            </Button>
          </>
        }
      />

      {job.is_public ? (
        <p className="type-body text-muted-foreground">
          Public URL:{" "}
          <Link
            href={`/hiring/jobs/${job.slug}?public=1`}
            className="underline"
            target="_blank"
          >
            /hiring/jobs/{job.slug}
          </Link>
        </p>
      ) : null}

      <JobActionsBar job={job} />

      <StatStrip
        variant="board"
        title={
          <span className="flex items-center justify-between gap-2">
            <span>Pipeline summary</span>
            <span className="type-body font-normal text-muted-foreground">
              {total} candidate{total === 1 ? "" : "s"}
            </span>
          </span>
        }
        items={pipelineStats}
      />

      <Card>
        <CardHeader>
          <CardTitle className="type-title">Description</CardTitle>
        </CardHeader>
        <CardContent>
          {job.description ? (
            <pre className="type-body whitespace-pre-wrap font-mono text-foreground">
              {job.description}
            </pre>
          ) : (
            <p className="type-body text-muted-foreground">No description yet.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
